import { Formik, FormikHelpers, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, Select } from "formik-antd";
import { Integration } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useSetting from "/app/src/components/settings/setting";

interface FormValues {
  value: string | undefined;
}

/**
 * Component to display the JoinType setting for a single Data Pull Integration
 */
export default function JoinType({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const { settingValue, createUpdateSetting } = useSetting({
    integrationId: integration.id,
    type: "joinType",
  });

  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await createUpdateSetting({
        ...values,
        type: "joinType",
        integrationId: integration.id,
      }).then(() => {
        actions.resetForm();
      });
    },
    [createUpdateSetting, integration.id],
  );

  const form: (props: FormikProps<FormValues>) => JSX.Element = useCallback(
    ({ isSubmitting, dirty }) => (
      <Form layout="vertical">
        <h3>{t("translation:join_behaviour")}</h3>
        <p>{t("translation:join_behaviour_description")}</p>
        <Row gutter={16}>
          <Col span={20}>
            <Form.Item name="value">
              <Select name="value" size="large">
                <Select.Option value="outer">
                  {t("translation:keep_all")}
                </Select.Option>
                <Select.Option value="inner">
                  {t("translation:keep_only_joined")}
                </Select.Option>
                <Select.Option value="left">
                  {t("translation:keep_joined_and_powerpick")}
                </Select.Option>
                <Select.Option value="right">
                  {t("translation:keep_joined_and_external")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <SubmitButton
              type="primary"
              size="large"
              block
              disabled={!dirty || isSubmitting}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );
  return (
    <Formik
      initialValues={{ value: settingValue ? settingValue : "outer" }}
      validationSchema={simpleSchemaBuilder([
        { name: "value", type: "string", required: true },
      ])}
      onSubmit={onSubmit}
      component={form}
      enableReinitialize
    />
  );
}
